import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  FiArrowRight,
  FiArrowUpRight,
  FiArrowDown,
  FiPhone,
  FiDatabase,
  FiGrid,
  FiShare2,
  FiBox,
  FiCreditCard,
  FiLayers,
  FiAward,
  FiClock,
  FiGlobe,
  FiTrendingUp,
} from 'react-icons/fi';
import SEO from '../components/SEO';
import './Home.css';

const AnimatedText = ({ children, className }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const AnimatedSection = ({ children, className, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// One editorial row. Case study rows link internally, live products
// open in a new tab, and prototypes with no public URL are static.
const ProjectRow = ({ project, index }) => {
  const rowClass = `portfolio-editorial-row ${
    index % 2 === 1 ? 'portfolio-editorial-row--reverse' : ''
  } ${project.url || project.caseStudySlug ? '' : 'sfh-row--static'} ${
    project.mobileImage ? '' : 'sfh-row--no-phone'
  }`;

  const ctaLabel = project.caseStudySlug
    ? 'View Case Study'
    : project.url
      ? 'View Project'
      : project.ctaNote;

  const rowInner = (
    <>
      <div className="portfolio-editorial-row__media">
        <img
          src={project.image}
          alt={project.title}
          className="portfolio-editorial-row__image"
          loading="lazy"
        />
        {project.mobileImage && (
          <img
            src={project.mobileImage}
            alt={`${project.title} on mobile`}
            className="portfolio-editorial-row__mobile"
            loading="lazy"
          />
        )}
      </div>

      <div className="portfolio-editorial-row__body">
        <p className="portfolio-editorial-row__category">{project.category}</p>
        <h3 className="portfolio-editorial-row__title">{project.title}</h3>
        <p className="portfolio-editorial-row__description">{project.description}</p>
        {project.note && <p className="sfh-row__note">{project.note}</p>}
        <span className="portfolio-editorial-row__cta">
          {ctaLabel} {(project.url || project.caseStudySlug) && <FiArrowUpRight />}
        </span>
      </div>
    </>
  );

  if (project.caseStudySlug) {
    return (
      <Link to={`/portfolio/${project.caseStudySlug}`} className={rowClass}>
        {rowInner}
      </Link>
    );
  }
  if (project.url) {
    return (
      <a href={project.url} target="_blank" rel="noopener noreferrer" className={rowClass}>
        {rowInner}
      </a>
    );
  }
  return <div className={rowClass}>{rowInner}</div>;
};

const Home = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const proofPoints = [
    {
      icon: <FiAward />,
      value: 'Palantir Certified',
      label: 'Application Developer Associate and Foundry Aware',
    },
    {
      icon: <FiClock />,
      value: '10+ years',
      label: 'Designing and building software',
    },
    {
      icon: <FiGlobe />,
      value: '8 products',
      label: 'Live in production today',
    },
    {
      icon: <FiTrendingUp />,
      value: '$100K grant',
      label: 'Awarded to a nonprofit client after launch',
    },
  ];

  const operationsCapabilities = [
    {
      icon: <FiDatabase />,
      title: 'Ontology design',
      description:
        'We model your business as objects and links, so leases, work orders, parts and payments can be queried together.',
    },
    {
      icon: <FiGrid />,
      title: 'Operational dashboards',
      description:
        'React apps on the Foundry OSDK that open on the answer: what is late, what it costs, and who owns it.',
    },
    {
      icon: <FiShare2 />,
      title: 'Actions and write-back',
      description:
        'Functions and action types let your team act on the data where they read it, with every change recorded.',
    },
  ];

  const productCapabilities = [
    {
      icon: <FiBox />,
      title: 'Full-stack products',
      description:
        'MERN and TypeScript apps with accounts, admin tools and email, taken from first sketch to production.',
    },
    {
      icon: <FiCreditCard />,
      title: 'Payments and fintech',
      description:
        'Stripe subscriptions and marketplaces, Plaid, banking APIs and KYC, wired in and tested end to end.',
    },
    {
      icon: <FiLayers />,
      title: 'UX and interface design',
      description:
        'Research-led flows and design systems, so the product is easy to use on day one and easy to extend later.',
    },
  ];

  const operationsProjects = [
    {
      id: 'invitationhomes',
      title: 'Invitation Homes Asset Manager',
      category: 'Palantir Foundry | Single-Family Rental',
      description:
        'An asset-management platform for a 3,000-home rental portfolio. It joins leases, rent payments, maintenance and tax bills to show which homes underperform their market, and whether the cause is pricing or cost.',
      note: 'Independent prototype on synthetic data. Not affiliated with Invitation Homes.',
      image: '/images/portfolio/invitationhomes-macbook.webp',
      ctaNote: 'Walkthrough on request',
    },
    {
      id: 'targetair',
      title: 'Target Air Program Operations',
      category: 'Palantir Foundry | Manufacturing',
      description:
        'A program dashboard for an aircraft production line. Shortages link to parts, parts to work orders, and work orders to delivery dates, so one screen answers what is blocking the line and what it costs.',
      note: 'Concept build for a fictional manufacturer, on generated data.',
      image: '/images/portfolio/targetair-macbook.webp',
      ctaNote: 'Walkthrough on request',
    },
  ];

  const productProjects = [
    {
      id: 'hometaxpro',
      title: 'HomeTaxPro',
      category: 'Web Application',
      description:
        'Property tax protest platform. Homeowners check their appraisal against county data and comparable sales, subscribe, and download a filing-ready protest PDF generated by Claude.',
      image: 'https://silverfoxmedia.s3.amazonaws.com/htphomepageonmac.png',
      mobileImage:
        "https://silverfoxmedia.s3.amazonaws.com/HomeTaxPro%20%E2%80%94%20America%27s%20%231%20Property%20Tax%20Protest%20Service%202-portrait.png",
      url: 'https://hometaxpro.com/',
      caseStudySlug: 'hometaxpro',
    },
    {
      id: 'babybunnyresale',
      title: 'Baby Bunny Resale',
      category: 'Marketplace',
      description:
        'Resale marketplace for baby gear with per-seller Stripe checkout, a slide-out cart, bulk listing import, watchlists, notifications and an admin platform.',
      image: '/images/portfolio/babybunnyresale-macbook.webp',
      url: 'https://babybunnyresale.com/',
      caseStudySlug: 'babybunnyresale',
    },
    {
      id: 'aishadow',
      title: 'AI Shadow',
      category: 'Enterprise SaaS',
      description:
        'Enterprise AI governance and data-loss prevention, enforced across the browser, the backend and the network, with a patent-pending risk-scoring engine.',
      image: 'https://silverfoxmedia.s3.amazonaws.com/aishadowmacbook.png',
      mobileImage:
        'https://silverfoxmedia.s3.amazonaws.com/AI%20Shadow%E2%84%A2%20%20Enterprise%20AI%20Governance%20%26%20DLP-portrait.png',
      url: 'https://aishadow.ai/',
      caseStudySlug: 'aishadow',
    },
    {
      id: 'gracetochange',
      title: 'Grace to Change',
      category: 'Non-Profit',
      description:
        'Full rebuild for a McKinney addiction-recovery nonprofit. WordPress was replaced with a Node and React CMS the team runs themselves. The City of McKinney awarded them a $100K grant after launch.',
      image: 'https://silverfoxmedia.s3.amazonaws.com/GTCnewsitehomepage2-front.png',
      url: 'https://gracetochange.org/',
      caseStudySlug: 'gracetochange',
    },
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Find the real problem',
      description:
        'We start on the floor with the people doing the work. The request is often a symptom of something upstream.',
    },
    {
      step: '02',
      title: 'Model it',
      description:
        'Before any screens, we map the data and how it connects. That map decides what the software can answer.',
    },
    {
      step: '03',
      title: 'Build and ship',
      description:
        'Working software in short cycles, deployed early, so you react to something real instead of a document.',
    },
    {
      step: '04',
      title: 'Check the numbers',
      description:
        'Every headline figure is recounted by a second path before launch. A wrong number on a dashboard costs trust fast.',
    },
  ];

  const stackGroups = [
    {
      title: 'Data and operations',
      items: ['Palantir Foundry', 'Ontology', 'OSDK', 'TypeScript Functions', 'Workshop', 'Python', 'MongoDB'],
    },
    {
      title: 'Product engineering',
      items: ['React', 'Node.js', 'Express', 'TypeScript', 'Stripe', 'Plaid', 'SendGrid'],
    },
    {
      title: 'Cloud and growth',
      items: ['AWS', 'Render', 'Cloudinary', 'Google Analytics 4', 'Search Console', 'WordPress', 'Shopify'],
    },
  ];

  return (
    <>
      <SEO
        title="Software, Data Platforms & Palantir Foundry Development"
        description="SilverFox Media builds products, internal tools and data platforms, including Palantir Foundry ontologies and OSDK apps. Palantir-certified, 10+ years in software, based in Dallas-Fort Worth."
        keywords="Palantir Foundry developer, Foundry OSDK, ontology design, custom software development, MERN development, web application development, UX design, Dallas software development"
        url="/"
      />

      {/* HERO SECTION */}
      {/* No scroll fade here: the audience panels are content, not
          decoration, and must stay fully visible while on screen. */}
      <section ref={heroRef} className="hero sfh-hero">
        <div className="hero__bg">
          <div
            className="hero__gradient-orb hero__gradient-orb--1"
            style={{
              transform: `translate(${mousePosition.x * 30}px, ${mousePosition.y * 30}px)`
            }}
          />
          <div
            className="hero__gradient-orb hero__gradient-orb--2"
            style={{
              transform: `translate(${mousePosition.x * -20}px, ${mousePosition.y * -20}px)`
            }}
          />
          <div className="hero__grid-overlay" />
        </div>

        <div className="container sfh-hero__inner">
          <motion.p
            className="hero__label sfh-hero__label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Software, data and design studio | Dallas-Fort Worth
          </motion.p>

          <h1 className="hero__title sfh-hero__title">
            <motion.span
              className="hero__title-line sfh-hero__title-line"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              We build the software
            </motion.span>
            <motion.span
              className="hero__title-line hero__title-line--accent sfh-hero__title-line"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              your business runs on.
            </motion.span>
          </h1>

          <motion.p
            className="hero__subtitle sfh-hero__subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
          >
            Customer-facing products, internal tools and data platforms on
            Palantir Foundry. Designed around the people who use them, and
            shipped to production.
          </motion.p>

          {/* Two doors, one per audience. Each panel carries its own
              proof so neither visitor lands on the other's screenshot. */}
          <motion.div
            className="sfh-hero__panels"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
          >
            <a href="#operations" className="sfh-hero__panel sfh-hero__panel--ops">
              <div className="sfh-hero__panel-media sfh-hero__panel-media--device">
                <span className="sfh-hero__panel-tag sfh-hero__panel-tag--light">
                  <FiDatabase /> Built on Palantir Foundry
                </span>
                <img
                  src="/images/portfolio/targetair-macbook.webp"
                  alt="Target Air program operations dashboard built on Palantir Foundry, on a laptop"
                  className="sfh-hero__panel-image sfh-hero__panel-image--device"
                />
              </div>
              <div className="sfh-hero__panel-body">
                <span className="sfh-hero__panel-kicker">I run operations</span>
                <span className="sfh-hero__panel-text">
                  Connect the systems you already have and see what is really
                  holding things up.
                </span>
                <span className="sfh-hero__panel-source">
                  Shown: Target Air program dashboard, a concept build on generated data
                </span>
                <span className="sfh-hero__panel-link">
                  Data and Foundry work <FiArrowDown />
                </span>
              </div>
            </a>

            <a href="#products" className="sfh-hero__panel sfh-hero__panel--products">
              <div className="sfh-hero__panel-media sfh-hero__panel-media--device">
                <span className="sfh-hero__panel-tag sfh-hero__panel-tag--light">
                  <FiGlobe /> Live at hometaxpro.com
                </span>
                <img
                  src="/images/portfolio/hometaxpro-macbook.webp"
                  alt="HomeTaxPro property tax protest platform on a laptop"
                  className="sfh-hero__panel-image sfh-hero__panel-image--device"
                />
              </div>
              <div className="sfh-hero__panel-body">
                <span className="sfh-hero__panel-kicker">I'm building a product</span>
                <span className="sfh-hero__panel-text">
                  Take an idea to a product customers sign up for and pay for.
                </span>
                <span className="sfh-hero__panel-source">
                  Shown: HomeTaxPro, a property tax protest platform we designed and built
                </span>
                <span className="sfh-hero__panel-link">
                  Products we've shipped <FiArrowDown />
                </span>
              </div>
            </a>
          </motion.div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="sfh-proof">
        <div className="container">
          <div className="sfh-proof__grid">
            {proofPoints.map((point, index) => (
              <AnimatedSection key={point.value} delay={index * 0.1} className="sfh-proof__item">
                <span className="sfh-proof__icon">{point.icon}</span>
                <span className="sfh-proof__value">{point.value}</span>
                <span className="sfh-proof__label">{point.label}</span>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FOR OPERATIONS TEAMS */}
      <section id="operations" className="sfh-audience sfh-audience--ops">
        <div className="container">
          <div className="sfh-audience__intro">
            <AnimatedText className="section-header sfh-audience__header">
              <p className="section-label">For operations and data teams</p>
              <h2 className="section-title">
                Answers that live <span className="text-gradient">between your systems</span>
              </h2>
              <p className="section-subtitle">
                Your ERP knows the part. Your MES knows the work order. Neither
                knows which delivery the missing part will push back. We build on
                Palantir Foundry to join those records into one model, then put an
                app in front of it that your team will actually open.
              </p>
            </AnimatedText>

            <div className="sfh-capabilities">
              {operationsCapabilities.map((item, index) => (
                <AnimatedSection key={item.title} delay={index * 0.1} className="sfh-capability">
                  <span className="sfh-capability__icon">{item.icon}</span>
                  <h3 className="sfh-capability__title">{item.title}</h3>
                  <p className="sfh-capability__text">{item.description}</p>
                </AnimatedSection>
              ))}
            </div>
          </div>

          <div className="portfolio-editorial sfh-audience__work">
            {operationsProjects.map((project, index) => (
              <AnimatedSection key={project.id} delay={index * 0.1}>
                <ProjectRow project={project} index={index} />
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FOR FOUNDERS AND GROWING BUSINESSES */}
      <section id="products" className="sfh-audience sfh-audience--products">
        <div className="portfolio-section__bg" />
        <div className="container">
          <div className="sfh-audience__intro">
            <AnimatedText className="section-header sfh-audience__header">
              <p className="section-label">For founders and growing businesses</p>
              <h2 className="section-title">
                Products people <span className="text-gradient">sign up for and pay for</span>
              </h2>
              <p className="section-subtitle">
                Marketplaces, fintech, SaaS and member platforms. We handle the
                design, the build, payments, hosting and what happens after launch,
                so you work with one team from the first sketch onward.
              </p>
            </AnimatedText>

            <div className="sfh-capabilities">
              {productCapabilities.map((item, index) => (
                <AnimatedSection key={item.title} delay={index * 0.1} className="sfh-capability">
                  <span className="sfh-capability__icon">{item.icon}</span>
                  <h3 className="sfh-capability__title">{item.title}</h3>
                  <p className="sfh-capability__text">{item.description}</p>
                </AnimatedSection>
              ))}
            </div>
          </div>

          <div className="portfolio-editorial sfh-audience__work">
            {productProjects.map((project, index) => (
              <AnimatedSection key={project.id} delay={index * 0.1}>
                <ProjectRow project={project} index={index} />
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection className="portfolio-section__cta">
            <Link to="/portfolio" className="btn-outline-glow btn-outline-glow--light">
              <span>View All Projects</span>
              <FiArrowRight />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="sfh-process">
        <div className="container">
          <div className="sfh-process__layout">
            <AnimatedText className="sfh-process__intro">
              <p className="section-label">How we work</p>
              <h2 className="section-title">We start with the problem, not the tool</h2>
              <p className="sfh-process__founder">
                SilverFox Media is led by James McEwen, a Palantir-certified
                application developer with more than ten years in software, UX and
                web development. You work with the people building your project,
                from the first call to launch day.
              </p>
            </AnimatedText>

            <div className="sfh-process__steps">
              {processSteps.map((item, index) => (
                <AnimatedSection key={item.step} delay={index * 0.1} className="sfh-process__step">
                  <span className="sfh-process__number">{item.step}</span>
                  <div>
                    <h3 className="sfh-process__title">{item.title}</h3>
                    <p className="sfh-process__text">{item.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STACK */}
      <section className="sfh-stack">
        <div className="container">
          <AnimatedText className="section-header section-header--center">
            <p className="section-label">Stack</p>
            <h2 className="section-title">What we build with</h2>
          </AnimatedText>

          <div className="sfh-stack__groups">
            {stackGroups.map((group, index) => (
              <AnimatedSection key={group.title} delay={index * 0.1} className="sfh-stack__group">
                <h3 className="sfh-stack__group-title">{group.title}</h3>
                <ul className="sfh-stack__list">
                  {group.items.map((item) => (
                    <li key={item} className="sfh-stack__chip">{item}</li>
                  ))}
                </ul>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* WEBSITES AND TEMPLATES */}
      <section className="sfh-websites">
        <div className="container">
          <AnimatedSection className="sfh-websites__card">
            <div className="sfh-websites__text">
              <p className="section-label">Websites and templates</p>
              <h2 className="sfh-websites__title">Need a website, not a platform?</h2>
              <p className="sfh-websites__body">
                We still design and build fast, search-ready marketing sites. Or start
                from one of our ready-made templates for law firms, real estate,
                apartments and clinics.
              </p>
            </div>
            <div className="sfh-websites__actions">
              <Link to="/templates" className="btn-glow">
                <span>Browse Templates</span>
                <FiArrowRight />
              </Link>
              <Link to="/pricing" className="btn-outline-glow">
                <span>See Pricing</span>
                <FiArrowUpRight />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="cta-section">
        <div className="cta-section__bg">
          <div className="cta-section__gradient-1" />
          <div className="cta-section__gradient-2" />
          <div className="cta-section__grid-overlay" />
        </div>
        <div className="container">
          <AnimatedText className="cta-section__content">
            <h2 className="cta-section__title">
              Tell us what is <span className="text-gradient">slowing you down</span>
            </h2>
            <p className="cta-section__text">
              A product that needs building, or a question your systems can't answer
              yet. Tell us where it hurts and we'll tell you how we'd approach it.
            </p>
            <div className="cta-section__buttons">
              <Link to="/contact" className="btn-glow btn-glow--large">
                <span>Start a Conversation</span>
                <FiArrowRight />
              </Link>
              <a href="tel:9728008105" className="btn-phone">
                <FiPhone />
                <span>(972) 800-8105</span>
              </a>
            </div>
          </AnimatedText>
        </div>
      </section>
    </>
  );
};

export default Home;
